import { Bounds } from "@nens/deck-gl";

import { Viz, lat2tile, long2tile, tile2lat, tile2long } from "./terrainMain";

export const createViz = (extentCoordinates: number[][], zoom: number, savedVizzes: Viz[]) => {
  const tLat = Math.floor(lat2tile(extentCoordinates[1][1], zoom));
  const tLatEnd = Math.floor(lat2tile(extentCoordinates[0][1], zoom));

  const tLong = Math.floor(long2tile(extentCoordinates[0][0], zoom));
  const tLongEnd = Math.floor(long2tile(extentCoordinates[1][0], zoom));

  // Find the amount of tiles needed in x and y direction
  let tileAmountX = 0;
  let stopX = false;
  while (!stopX) {
    tileAmountX++;
    if (tLong + tileAmountX - 1 > tLongEnd) stopX = true;
  }
  let tileAmountY = 0;
  let stopY = false;
  while (!stopY) {
    tileAmountY++;
    if (tLat + tileAmountY - 1 > tLatEnd) stopY = true;
  }

  const canvasSize = tileAmountX * 256 * tileAmountY * 256;

  // If canvas is bigger then 100 mil pixels retrieve new canvas on every move
  // If canvas is bigger then 25 mil pixels split up the canvas
  // If canvas is bigger then 4 mil pixels crop the canvas to viewport
  const type =
    canvasSize > 100000000
      ? "enormous"
      : canvasSize > 250000000
      ? "giant"
      : canvasSize > 4000000
      ? "cropped"
      : "normal";
  const createCanvas = (amountX: number, amountY: number) => {
    const canvas = new OffscreenCanvas(amountX * 256, amountY * 256);
    const ctx = canvas.getContext("2d")! as OffscreenCanvasRenderingContext2D;
    return [canvas, ctx];
  };

  const makeCanvas = () => {
    if (type === "giant") {
      const halfTileAmountX = Math.floor(tileAmountX / 2);
      const halfTileAmountY = Math.floor(tileAmountY / 2);
      const [canvasLeftTop, ctxLeftTop] = createCanvas(halfTileAmountX, halfTileAmountY);
      const [canvasRightTop, ctxRightTop] = createCanvas(
        tileAmountX - halfTileAmountX,
        halfTileAmountY
      );
      const [canvasLeftBottom, ctxLeftBottom] = createCanvas(
        halfTileAmountX,
        tileAmountY - halfTileAmountY
      );
      const [canvasRightBottom, ctxRightBottom] = createCanvas(
        tileAmountX - halfTileAmountX,
        tileAmountY - halfTileAmountY
      );
      return [
        [canvasLeftTop, canvasRightTop, canvasLeftBottom, canvasRightBottom],
        [ctxLeftTop, ctxRightTop, ctxLeftBottom, ctxRightBottom],
      ];
    }
    return createCanvas(tileAmountX, tileAmountY);
  };
  const [canvasNS, ctxNS] = makeCanvas();
  const [canvasDEM, ctxDem] = makeCanvas();

  const newBounds = [
    tile2long(tLong - 1, zoom),
    tile2lat(tLat - 1, zoom),
    tile2long(tLong + tileAmountX - 1, zoom),
    tile2lat(tLat + tileAmountY - 1, zoom),
  ] as Bounds;

  const newViz = {
    zoomLevel: zoom,
    canvasNS,
    canvasDEM,
    ctxNS,
    ctxDem,
    newBounds,
    tLong,
    tLat,
    tileAmountX,
    tileAmountY,
    visualizedXYNS: [],
    visualizedXYDEM: [],
    type,
  } as Viz;

  // If canvas is combined bigger then 2.5 mil pixels crop the canvas to viewport
  if (newViz.type !== "normal") {
    newViz.croppedCanvasNS = new OffscreenCanvas(2000, 2000);
    newViz.croppedCanvasDEM = new OffscreenCanvas(2000, 2000);
    newViz.croppedCtxNS = newViz.croppedCanvasNS.getContext("2d", {
      alpha: false,
    })! as OffscreenCanvasRenderingContext2D;
    newViz.croppedCtxDem = newViz.croppedCanvasDEM.getContext(
      "2d"
    )! as OffscreenCanvasRenderingContext2D;
  }

  savedVizzes.push(newViz);
  return newViz;
};
